//Stores
import { get } from 'svelte/store';
import { modalStore } from 'src/stores/ui/modal_states';
import { profile_data, user_data, user_settings } from 'src/stores/account/user';
import { profiles } from 'src/stores/features/profiles';
//Services
import { format, locale } from 'svelte-i18n';
import { postReq } from 'src/services/http';
//Design System Components
import { toast } from '@Shelly/shelly-design-system/Toast/toast';
//Helpers
import { ShellyBirthdayCampaignModalConfig } from 'src/configs/modals/ShellyBirthdayCampaignModalConfig';
import { ShellyBirthdayTeaserModalConfig } from 'src/configs/modals/ShellyBirthdayTeaserModalConfig';
import { SmartHomeChallengeModalConfig } from 'src/configs/modals/SmartHomeChallengeModalConfig';
import { SmartHomeChallenge2026ModalConfig } from 'src/configs/modals/SmartHomeChallenge2026ModalConfig';
import { ShellyBirthday2026ModalConfig } from 'src/configs/modals/ShellyBirthday2026ModalConfig';
import { SpringDeals2026ModalConfig } from 'src/configs/modals/SpringDeals2026ModalConfig';
import { ThirtyMillionDevicesModalConfig } from 'src/configs/modals/ThirtyMillionDevicesModalConfig';
import { XlX2FlashDealModalConfig } from 'src/configs/modals/XlX2FlashDealModalConfig';
import { WallDisplayFlashSale2ModalConfig } from 'src/configs/modals/WallDisplayFlashSale2ModalConfig';
import { Gen1ReplacementConfig } from 'src/configs/modals/Gen1ReplacementConfig';
import { BlackFridayPhase1Config } from 'src/configs/modals/BlackFridayPhase1Config';
import { BlackFridayPhase2Config } from 'src/configs/modals/BlackFridayPhase2Config';
import { AnnualSurvey2026Config } from 'src/configs/modals/AnnualSurvey2026Config';
import { VerivoxSetupModalConfig } from 'src/configs/modals/VerivoxSetupModalConfig';
import { PROMOTIONS } from 'src/constants';
import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
import { g1DHasValidExpiry } from 'src/helpers/home_helpers';
import { save_profile } from 'src/helpers/profile_helpers';
import { formatExpiryText, getPromoExpiryText } from 'src/helpers/promo_helpers';
import type { DiscountDetailsType, DiscountType } from 'src/types/DiscountType';
import { load_image_from } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import type { DeviceNotification } from 'src/helpers/notifications/DeviceNotification';
import type { NotificationMenuItemPropsTypes } from '@Shelly/shelly-design-system/index';
import type { NotificationType } from 'src/helpers/notifications/DeviceNotificationBuilder';
import { shouldShowVerivoxPromoBanner } from 'src/helpers/verivox_promo_helpers';

export { shouldShowVerivoxPromoBanner };

export type NotificationFilterType = 'all' | 'unread' | 'promo';

export type DeviceNotificationsExtended = (DeviceNotification & {
	date?: any;
	circleAvatar?: any;
})[];

export const TIME_TO_CLEAR_NOTIFICATION = 86400; // 24 hours to clear seen notifications by default

export const NOTIFICATION_FILTER: {
	[key in NotificationFilterType]: NotificationFilterType;
} = {
	all: 'all',
	unread: 'unread',
	promo: 'promo',
};

export const genNotificationItemAvatar = (device: Device) => {
	if (!device) return {};
	return {
		image: device?.image
			? load_image_from(device?.image, device?.localImage ? '' : device?.server).replace(
					'thumb_',
					'',
				)
			: undefined,
		backgroundColor: device.backgroundColor ? device.backgroundColor : null,
		icon: device.icon,
	};
};

export const badgeNotificationItemMapper: {
	[key in NotificationType]: NotificationMenuItemPropsTypes['badge'];
} = {
	promo: {
		textClass: 'text-primary',
		value: 'Promo',
	},
	danger: {
		textClass: 'text-danger',
		value: 'Danger',
	},
	warning: {
		textClass: 'text-warning',
		value: 'Warning',
	},
	info: {
		textClass: 'text-primary',
		value: 'Info',
	},
	success: {
		textClass: 'text-success',
		value: 'Success',
	},
};

export const genNotificationItemBadge = (
	notification: DeviceNotification,
): NotificationMenuItemPropsTypes['badge'] => {
	return badgeNotificationItemMapper[notification.type];
};

export const getActiveProfileID = () => {
	const activeProfile = get(profiles).find((item) => item.isSelected);
	return activeProfile.id;
};

// Replace the showModal bindings with modalStore actions
export const showSmartHomeChallenge = () => {
	modalStore.showModal(SmartHomeChallengeModalConfig());
};

export const showBirthdayTeaser = () => {
	modalStore.showModal(ShellyBirthdayTeaserModalConfig());
};

export const showBirthdayCampaign = () => {
	modalStore.showModal(ShellyBirthdayCampaignModalConfig());
};

export const sortByDateProp = (a: { date: Date }, b: { date: Date }) => {
	return new Date(b.date).getTime() - new Date(a.date).getTime();
};

export const generateNotificationHTMLContent = (title: string, message: string = undefined) => {
	return `<div class="text-bold">${title}</div>
			${message ? `<div class="mt-xs">${message}</div>` : ''}
    `;
};

const showSpringDeals2026 = () => {
	modalStore.showModal(SpringDeals2026ModalConfig(true));
};

const showSmartHomeChallenge2026 = () => {
	modalStore.showModal(SmartHomeChallenge2026ModalConfig(true));
};

const showShellyBirthday2026 = () => {
	modalStore.showModal(ShellyBirthday2026ModalConfig(true));
};

const showThirtyMillionDevices = () => {
	modalStore.showModal(ThirtyMillionDevicesModalConfig(true));
};

const showXlX2FlashDeal = () => {
	modalStore.showModal(XlX2FlashDealModalConfig(true));
};

const showWallDisplayFlashSale2 = () => {
	modalStore.showModal(WallDisplayFlashSale2ModalConfig(true));
};

const showGen1Replacement = () => {
	modalStore.showModal(Gen1ReplacementConfig(true));
};

const showBlackFridayPhase1 = () => {
	modalStore.showModal(BlackFridayPhase1Config(true));
};

const showBlackFridayPhase2 = () => {
	modalStore.showModal(BlackFridayPhase2Config(true));
};

const showAnnualSurvey2026 = () => {
	modalStore.showModal(AnnualSurvey2026Config());
};

const showVerivoxSetup = () => {
	modalStore.showModal(VerivoxSetupModalConfig(true));
};

export { showVerivoxSetup };

/**
 * Mirrors the per-offer "seen" bookkeeping performed when a promo is clicked
 * inside the legacy Special Offers dropdown, so relocating offers to the
 * Notifications surface keeps the existing offers lifecycle intact.
 */
const markAepCampaignClicked = (key: 'thm' | 'xlx2' | 'wdfs2' | 'sd' | 'shc26' | 'bd26') => {
	const currentAep = get(profile_data)?.aep;
	const campaign = currentAep?.[key] ? { ...currentAep[key] } : {};
	campaign.seen = new Date().getTime();
	campaign.clickOnDropdownMenuItem = (campaign?.clickOnDropdownMenuItem || 0) + 1;
	save_profile('aep', { ...currentAep, [key]: campaign });
};

/** Resolve a PROMOTIONS END_DATE into the localized "Expires in X days" label. */
const resolvePromoExpiryText = (endDate: number): string | undefined => {
	const expiry = getPromoExpiryText(endDate);
	return expiry ? get(format)(expiry.key, expiry.object) : undefined;
};

const PROMO_AVATAR_ICON = { icon: 'fas fa-badge-percent text-primary' };

/**
 * Builds the list of currently active Special Offers as native promo
 * notification items so they render inside the Notifications surface and
 * participate in the shared seen/unread + counter logic. The offers data,
 * fetching and lifecycle (PROMOTIONS windows, profile_data/aep state) remain the
 * source of truth — this only adapts their presentation.
 */
export const getPromoNotifications = (): DeviceNotificationsExtended => {
	const now = new Date().getTime();
	const notificationsPromos: any[] = [];

	const userData = get(user_data);
	const isUsMarket = userData?.timezone?.startsWith('America/');
	const currentLocale = get(locale);
	const springDeals2026MaxDiscount = isUsMarket ? 50 : 40;
	const springDeals2026Thumbnail = isUsMarket
		? './images/shelly_spring_deals_2026_App_thumbnail_us.png'
		: './images/shelly_spring_deals_2026_App_thumbnail.png';
	const smartHomeChallenge2026Thumbnail = isUsMarket
		? './images/ioT-Challenge-thumbnail-us.png'
		: `./images/ioT-Challenge-thumbnail-${
				['de', 'fr', 'it'].includes(currentLocale) ? currentLocale : 'default'
			}.png`;
	const smartHomeChallenge2026PrizeLabel = isUsMarket ? '$500' : '500€';
	const shellyBirthday2026Thumbnail = `./images/Shelly_BD_8y_KV_Adaptations_for_App_badge_${
		isUsMarket ? 'default' : ['de', 'fr', 'it'].includes(currentLocale) ? currentLocale : 'default'
	}.png`;

	const g1dItems = get(profile_data)?.aep?.g1d
		? Object.entries(get(profile_data).aep.g1d).map(([key, item]) => ({
				...(item as DiscountDetailsType),
				key,
			}))
		: [];

	if (
		g1dItems.length > 0 &&
		g1DHasValidExpiry(g1dItems) &&
		now >= PROMOTIONS.GEN_1_REPLACEMENT.START_DATE &&
		now <= PROMOTIONS.GEN_1_REPLACEMENT.END_DATE
	) {
		const g1dExpiry = formatExpiryText(g1dItems[0]?.expiry);
		notificationsPromos.push({
			id: 'gen1Replacement',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-percent text-primary' },
			date: g1dExpiry ? get(format)(g1dExpiry.key, g1dExpiry.object) : undefined,
			heading: {
				global: get(format)('new_title_gen1_replacement', {
					default: 'Gen1 Upgrade Programme',
				}),
			},
			message: get(format)('new_gen_1_replacement_message', {
				default: 'You have been chosen for discount to upgrade your Gen1 devices',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => showGen1Replacement(),
			},
		});
	}

	if (
		now >= PROMOTIONS.THIRTY_MILLION_DEVICES.START_DATE &&
		now < PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE
	) {
		notificationsPromos.push({
			id: 'thirtyMillionDevices',
			type: 'promo',
			circleAvatar: { image: './images/30m_thumbnail.png' },
			date: resolvePromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE),
			heading: {
				global: get(format)('heading_30_million_devices', {
					default: '30 Million Smart Decisions',
				}),
			},
			message: get(format)('text_30_million_devices_message', {
				default: 'Up to 30% Off. Buy more. Save more!',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('thm');
					showThirtyMillionDevices();
				},
			},
		});
	}

	if (
		(!!get(profile_data)?.aep?.xlx2?.ts || import.meta.env.DEV) &&
		now >= PROMOTIONS.XL_X2_FLASH_DEAL.START_DATE &&
		now < PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE
	) {
		notificationsPromos.push({
			id: 'xlX2FlashDeal',
			type: 'promo',
			circleAvatar: { image: './images/xl_x2_flash_deal_thumbnail.png' },
			date: resolvePromoExpiryText(PROMOTIONS.XL_X2_FLASH_DEAL.END_DATE),
			heading: {
				global: get(format)('title_wall_display_flash_sale', {
					default: 'WallDisplay Flash Sale',
				}),
			},
			message: get(format)('text_wall_display_flash_sale_message', {
				default: 'Your smart home deserves a control center.',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('xlx2');
					showXlX2FlashDeal();
				},
			},
		});
	}

	if (
		(!!get(profile_data)?.aep?.wdfs2?.code || import.meta.env.DEV) &&
		canManageHomeEntities() &&
		now >= PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.START_DATE &&
		now < PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE
	) {
		notificationsPromos.push({
			id: 'wallDisplayFlashSale2',
			type: 'promo',
			circleAvatar: { image: './images/wall_display_flash__deal_thumbnail.png' },
			date: resolvePromoExpiryText(PROMOTIONS.WALL_DISPLAY_FLASH_SALE_2.END_DATE),
			heading: {
				global: get(format)('title_wall_display_flash_sale_2', {
					default: 'Wall Display Flash Sale',
				}),
			},
			message: get(format)('text_wall_display_flash_sale_2_message', {
				default: 'An Exclusive Offer for Our Most Loyal Users',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('wdfs2');
					showWallDisplayFlashSale2();
				},
			},
		});
	}

	if (now >= PROMOTIONS.SPRING_DEALS_2026.START_DATE && now < PROMOTIONS.SPRING_DEALS_2026.END_DATE) {
		notificationsPromos.push({
			id: 'springDeals2026',
			type: 'promo',
			circleAvatar: { image: springDeals2026Thumbnail },
			date: resolvePromoExpiryText(PROMOTIONS.SPRING_DEALS_2026.END_DATE),
			heading: {
				global: get(format)('title_spring_deals', { default: 'Spring Deals' }),
			},
			message: get(format)('text_spring_deals_message', {
				default: 'Fresh season. Best prices. Up to {percent}% off.',
				values: { percent: springDeals2026MaxDiscount },
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('sd');
					showSpringDeals2026();
				},
			},
		});
	}

	if (
		now >= PROMOTIONS.SMART_HOME_CHALLENGE_2026.START_DATE &&
		now < PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE
	) {
		notificationsPromos.push({
			id: 'smartHomeChallenge2026',
			type: 'promo',
			circleAvatar: { image: smartHomeChallenge2026Thumbnail },
			date: resolvePromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE_2026.END_DATE),
			heading: {
				global: get(format)('title_smart_home_challenge_2026', {
					default: 'Smart Home Challenge 2026',
				}),
			},
			message: get(format)('text_smart_home_challenge_2026_message', {
				default: 'Submit Your Project And Win Attractive Prizes!',
				values: { prize: smartHomeChallenge2026PrizeLabel },
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('shc26');
					showSmartHomeChallenge2026();
				},
			},
		});
	}

	if (
		now >= PROMOTIONS.SHELLY_BIRTHDAY_2026.START_DATE &&
		now < PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE
	) {
		notificationsPromos.push({
			id: 'shellyBirthday2026',
			type: 'promo',
			circleAvatar: { image: shellyBirthday2026Thumbnail },
			date: resolvePromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE),
			heading: {
				global: get(format)('title_shelly_birthday_2026', {
					default: '8 years deserve big deals',
				}),
			},
			message: get(format)(
				isUsMarket ? 'text_shelly_birthday_2026_intro_us' : 'text_shelly_birthday_2026_intro',
				{
					default: isUsMarket
						? 'Celebrate with us and enjoy up to 50% off, free gifts, and more'
						: 'Celebrate with us and enjoy up to -50%, free gifts, and more',
				},
			),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => {
					markAepCampaignClicked('bd26');
					showShellyBirthday2026();
				},
			},
		});
	}

	if (
		now >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE &&
		now < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE
	) {
		notificationsPromos.push({
			id: 'homeChallenge',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-trophy-star text-primary' },
			date: resolvePromoExpiryText(PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE),
			heading: {
				global: get(format)('heading_smart_home_challenge', {
					default: 'Join the Shelly Smart Home Challenge',
				}),
			},
			message: get(format)('message_smart_home_challenge', {
				default: 'Show off your ideas and win!',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => showSmartHomeChallenge(),
			},
		});
	} else if (
		now >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE &&
		now < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE
	) {
		notificationsPromos.push({
			id: 'bdayTeaser',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-gift text-primary' },
			date: resolvePromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE),
			heading: {
				global: get(format)('alert_heading_birthday_invitation', {
					default: "It's our birthday soon-and you're invited!",
				}),
			},
			message: get(format)('alert_message_join_for_present', {
				default: 'Join the VIP guest list and get your special present!',
			}),
			ctaGlobal: {
				title: get(format)('link_save_your_spot', { default: 'Save your spot' }),
				onClick: () => showBirthdayTeaser(),
			},
		});
	} else if (
		now >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE &&
		now < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE
	) {
		notificationsPromos.push({
			id: 'bdayCampaign',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-cake-candles text-primary' },
			date: resolvePromoExpiryText(PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE),
			heading: {
				global: get(format)('alert_heading_shelly_birthday', {
					default: 'Happy Shelly Birthday!',
				}),
			},
			message: get(format)('alert_message_seven_years', {
				default: '7 Years of Innovation, 7 Days of Celebration',
			}),
			ctaGlobal: {
				title: get(format)('link_join_party', { default: 'Join the party' }),
				onClick: () => showBirthdayCampaign(),
			},
		});
	} else if (
		now >= PROMOTIONS.BLACK_FRIDAY_PHASE_1.START_DATE &&
		now < PROMOTIONS.BLACK_FRIDAY_PHASE_1.END_DATE
	) {
		notificationsPromos.push({
			id: 'blackFridayPhase1',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-star text-primary' },
			date: resolvePromoExpiryText(PROMOTIONS.BLACK_FRIDAY_PHASE_1.END_DATE),
			heading: {
				global: get(format)('alert_heading_black_friday_phase_1', {
					default: 'Your VIP Black Friday access awaits',
				}),
			},
			message: get(format)('alert_message_black_friday_phase_1', {
				default: 'Join the exclusive guest list and unlock secret perks!',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => showBlackFridayPhase1(),
			},
		});
	} else if (
		now >= PROMOTIONS.ANNUAL_SURVEY_2026.START_DATE &&
		now < PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE
	) {
		notificationsPromos.push({
			id: 'annualSurvey2026',
			type: 'promo',
			circleAvatar: { icon: 'fas fa-user-microphone text-primary' },
			date: resolvePromoExpiryText(PROMOTIONS.ANNUAL_SURVEY_2026.END_DATE),
			heading: {
				global: get(format)('subtitle_one_annual_survey', { default: "It's feedback time!" }),
			},
			message: get(format)('alert_message_annual_survey_2026', {
				default: 'Join our quick survey and enjoy 15% off your next Shelly order',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => showAnnualSurvey2026(),
			},
		});
	} else if (
		now >= PROMOTIONS.BLACK_FRIDAY_PHASE_2.START_DATE &&
		now < PROMOTIONS.BLACK_FRIDAY_PHASE_2.END_DATE
	) {
		notificationsPromos.push({
			id: 'blackFridayPhase2',
			type: 'promo',
			circleAvatar: PROMO_AVATAR_ICON,
			date: resolvePromoExpiryText(PROMOTIONS.BLACK_FRIDAY_PHASE_2.END_DATE),
			heading: {
				global: get(format)('alert_heading_black_friday_phase_2', {
					default: 'Black Friday starts now',
				}),
			},
			message: get(format)('alert_message_black_friday_phase_2', {
				default: 'Smart tech. Smarter savings.',
			}),
			ctaGlobal: {
				title: get(format)('link_learn_more', { default: 'Learn more' }),
				onClick: () => showBlackFridayPhase2(),
			},
		});
	}

	return notificationsPromos;
};

export const setNotificationHomeData = async () => {
	if (
		new Date().getTime() >= PROMOTIONS.SPIN_WHEEL.START_DATE &&
		new Date().getTime() < PROMOTIONS.SPIN_WHEEL.END_DATE &&
		!get(profile_data)?.promotions?.spinWheel
	) {
		save_profile('promotions', {
			...get(profile_data)?.promotions,
			spinWheel: new Date().getTime(),
		});
	}
	if (
		new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE &&
		new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE &&
		!get(profile_data)?.promotions?.smartHomeChallenge
	) {
		save_profile('promotions', {
			...get(profile_data)?.promotions,
			smartHomeChallenge: new Date().getTime(),
		});
	}
	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE &&
		!get(profile_data)?.promotions?.shellyBirthdayTeaser
	) {
		save_profile('promotions', {
			...get(profile_data)?.promotions,
			shellyBirthdayTeaser: new Date().getTime(),
		});
	}
	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE &&
		!get(profile_data)?.promotions?.shellyBirthdayCampaign
	) {
		save_profile('promotions', {
			...get(profile_data)?.promotions,
			shellyBirthdayCampaign: new Date().getTime(),
		});
	}
	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_2026.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE &&
		!get(profile_data)?.promotions?.shellyBirthday2026
	) {
		save_profile('promotions', {
			...get(profile_data)?.promotions,
			shellyBirthday2026: new Date().getTime(),
		});
	}

	const discountEntries = get(user_settings)?.aep?.nd
		? Object.entries(get(user_settings)?.aep?.nd)
		: [];
	const results = await Promise.all(
		discountEntries?.map(async ([promotion, values]) => {
			if (values?.seen) return true;
			const res = await postReq('user/set_mt_seen', {
				scope: 'nd',
				trigger: promotion,
			});
			return res?.isok;
		}),
	);

	const allSuccessful = results.every((result) => result === true);

	if (!allSuccessful) {
		toast.open({ type: 'danger', title: get(format)('error_global') });
		return;
	}

	// const now = Math.floor(Date.now() / 1000);
	// const clonedUserCampaigns = $user_settings?.aep;
	// if (clonedUserCampaigns) {
	// 	Object.values(clonedUserCampaigns).forEach((campaign) => {
	// 		Object.values(campaign).forEach((discount) => {
	// 			if (!discount.hasOwnProperty('seen')) {
	// 				discount.seen = now;
	// 			}
	// 		});
	// 	});
	// 	user_settings.set({ ...$user_settings, aep: clonedUserCampaigns });
	// }

	if (
		allSuccessful &&
		get(user_settings)?.aep &&
		(!get(profile_data)?.aep ||
			JSON.stringify(get(profile_data)?.aep) !== JSON.stringify(get(user_settings)?.aep))
	) {
		save_profile('aep', { ...get(profile_data)?.aep, ...get(user_settings)?.aep });
	}
};

export const listenForActiveDiscounts = (user_settings, profileData) => {
	const discounts = (
		user_settings?.aep?.nd
			? Object.values(user_settings?.aep?.nd).filter((discount: DiscountType) => !discount?.seen)
			: []
	) as DiscountType[];

	let discountsNotification = 0;

	if (discounts.length > 0) {
		if (profileData?.aep?.nd) {
			if (Object.keys(user_settings?.aep?.nd).length > Object.keys(profileData?.aep?.nd).length) {
				discountsNotification =
					Object.keys(user_settings?.aep?.nd).length -
					Object.keys(profileData?.aep?.nd).length;
			} else {
				discountsNotification = 0;
			}
		} else {
			discountsNotification = discounts.length;
		}
	} else {
		discountsNotification = 0;
	}

	$: if (
		new Date().getTime() >= PROMOTIONS.SPIN_WHEEL.START_DATE &&
		new Date().getTime() < PROMOTIONS.SPIN_WHEEL.END_DATE
	) {
		if (!profileData?.promotions?.spinWheel) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	if (
		new Date().getTime() >= PROMOTIONS.SMART_HOME_CHALLENGE.START_DATE &&
		new Date().getTime() < PROMOTIONS.SMART_HOME_CHALLENGE.END_DATE
	) {
		if (!profileData?.promotions?.smartHomeChallenge) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_TEASER.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_TEASER.END_DATE
	) {
		if (!profileData?.promotions?.shellyBirthdayTeaser) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_CAMPAIGN.END_DATE
	) {
		if (!profileData?.promotions?.shellyBirthdayCampaign) {
			discountsNotification += 1;
		} else {
			if (discountsNotification > 0) {
				discountsNotification -= 1;
			} else {
				discountsNotification = 0;
			}
		}
	}

	if (
		new Date().getTime() >= PROMOTIONS.SHELLY_BIRTHDAY_2026.START_DATE &&
		new Date().getTime() < PROMOTIONS.SHELLY_BIRTHDAY_2026.END_DATE
	) {
		if (!profileData?.promotions?.shellyBirthday2026) {
			discountsNotification += 1;
		} else if (discountsNotification > 0) {
			discountsNotification -= 1;
		} else {
			discountsNotification = 0;
		}
	}

	return discountsNotification;
};
